import { NodeBadge, NodeRef, Title, TreeModel, TreeNodeModel, TreeSummary } from '../models/access.models';
import { titleIn } from './access-format';

/** The tree the client builds out of `GET /tree` (all the nodes at once): the parents, the sorted live children, the search over the titles. */
export interface TreeIndex {
  byId: ReadonlyMap<number, TreeNodeModel>;
  children: ReadonlyMap<number, readonly TreeNodeModel[]>;
  roots: readonly number[];
}

export type TreeIcon = 'globe' | 'flag' | 'city' | 'group' | 'dept' | 'store';

export interface TreeBadgeView { text: string; cls: 'cc' | 'mk' | 'fr' | 'wk' }

export interface TreeRowView {
  kind: 'node' | 'more';
  /** The id of the node; for the row «ещё N» — the id of the parent whose children it opens. */
  id: number;
  node: TreeNodeModel | null;
  depth: number;
  pad: string;
  icon: TreeIcon;
  color: string;
  open: boolean;
  closed: boolean;
  badges: TreeBadgeView[];
  count: number | null;
  countPeople: boolean;
  selected: boolean;
  moreCount: number;
  moreGroups: boolean;
}

export interface TreeViewOptions {
  expanded: Readonly<Record<number, boolean>>;
  selectedId: number | null;
  query: string;
  showAll: Readonly<Record<number, boolean>>;
  lang: string;
}

export const CHILD_LIMIT = 10;
const MIN_QUERY = 2;
const KIND_ORDER = { D: 0, G: 1, S: 2 } as const;
const ICON_COLORS: Record<TreeIcon, string> = { globe: '#334155', flag: '#334155', city: '#0f766e', group: '#6d28d9', dept: '#9a3412', store: '#1668dc' };
const BADGE_TEXT: Partial<Record<NodeBadge, TreeBadgeView>> = {
  callCenter: { text: 'access.badges.callCenter', cls: 'cc' },
  marketing: { text: 'access.badges.marketing', cls: 'mk' },
  franchise: { text: 'access.badges.franchise', cls: 'fr' },
  workshop: { text: 'access.badges.workshop', cls: 'wk' },
  secondRoot: { text: 'access.badges.secondRoot', cls: 'wk' },
  microStore: { text: 'access.badges.microStore', cls: 'wk' }
};

export function isLive(node: TreeNodeModel): boolean {
  return node.enabled && !node.removed;
}

export function buildTreeIndex(model: TreeModel, lang: string): TreeIndex {
  const byId = new Map<number, TreeNodeModel>(model.nodes.map(n => [n.id, n] as const));
  const grouped = new Map<number, TreeNodeModel[]>();
  for (const node of model.nodes) {
    if (node.parentId !== null && isLive(node)) {
      const list = grouped.get(node.parentId) ?? [];
      list.push(node);
      grouped.set(node.parentId, list);
    }
  }
  const children = new Map<number, readonly TreeNodeModel[]>();
  grouped.forEach((list, parentId) => children.set(parentId, [...list].sort((a, b) => compareNodes(a, b, lang))));
  return { byId, children, roots: model.roots };
}

function compareNodes(a: TreeNodeModel, b: TreeNodeModel, lang: string): number {
  const byKind = KIND_ORDER[a.kind] - KIND_ORDER[b.kind];
  return byKind !== 0 ? byKind : titleIn(a.title, lang).localeCompare(titleIn(b.title, lang), 'ru');
}

/** The ancestors of the node from the root down to its parent (the node itself is not included). */
export function ancestorsOf(index: TreeIndex, id: number): TreeNodeModel[] {
  const chain: TreeNodeModel[] = [];
  const seen = new Set<number>([id]);
  let current = index.byId.get(id);
  while (current && current.parentId !== null && !seen.has(current.parentId)) {
    const parent = index.byId.get(current.parentId);
    if (!parent) {
      break;
    }
    seen.add(parent.id);
    chain.unshift(parent);
    current = parent;
  }
  return chain;
}

export function pathRefs(index: TreeIndex, id: number): NodeRef[] {
  return ancestorsOf(index, id).map(n => ({ id: n.id, title: n.title, kind: n.kind }));
}

export function descendantsOf(index: TreeIndex, id: number): Set<number> {
  const result = new Set<number>();
  const stack = [id];
  while (stack.length > 0) {
    const current = stack.pop() as number;
    for (const child of index.children.get(current) ?? []) {
      if (!result.has(child.id)) {
        result.add(child.id);
        stack.push(child.id);
      }
    }
  }
  return result;
}

function depthOf(index: TreeIndex, node: TreeNodeModel): number {
  return ancestorsOf(index, node.id).length;
}

function iconOf(node: TreeNodeModel, depth: number): TreeIcon {
  if (node.kind === 'D') {
    return 'dept';
  }
  if (node.kind === 'S') {
    return 'store';
  }
  if (depth === 0) {
    return 'globe';
  }
  if (depth === 1) {
    return 'flag';
  }
  return node.badges.includes('franchise') || depth > 2 ? 'group' : 'city';
}

export function iconColor(icon: TreeIcon): string {
  return ICON_COLORS[icon];
}

/** The nodes the search leaves in the tree: the ones whose title contains the query and all their ancestors. */
export function searchMatches(index: TreeIndex, query: string, lang: string): Set<number> | null {
  const needle = query.trim().toLowerCase();
  if (needle.length < MIN_QUERY) {
    return null;
  }
  const matches = new Set<number>();
  index.byId.forEach(node => {
    const hit = [node.title.ru, node.title.kk, node.title.en, titleIn(node.title, lang)].some(t => !!t && t.toLowerCase().includes(needle));
    if (hit && isLive(node)) {
      matches.add(node.id);
      for (const parent of ancestorsOf(index, node.id)) {
        matches.add(parent.id);
      }
    }
  });
  return matches;
}

/** The rows of the tree as the board draws them: the chevron, the drawing, the title, the badge and the number at the right. */
export function buildTreeRows(index: TreeIndex, options: TreeViewOptions): TreeRowView[] {
  const rows: TreeRowView[] = [];
  const matches = searchMatches(index, options.query, options.lang);
  const onPath = new Set<number>(options.selectedId === null ? [] : [options.selectedId, ...ancestorsOf(index, options.selectedId).map(n => n.id)]);

  const visit = (node: TreeNodeModel, depth: number): void => {
    const kids = (index.children.get(node.id) ?? []).filter(k => matches === null || matches.has(k.id));
    const expandable = kids.length > 0;
    const open = expandable && (matches !== null || options.expanded[node.id] === true);
    const workshop = node.kind === 'S' && (index.children.get(node.id) ?? []).some(k => k.kind === 'S');
    const workshopPoints = workshop ? (index.children.get(node.id) ?? []).filter(k => k.kind === 'S').length : 0;
    rows.push({
      kind: 'node',
      id: node.id,
      node,
      depth,
      pad: `${8 + depth * 15}px`,
      icon: iconOf(node, depth),
      color: ICON_COLORS[iconOf(node, depth)],
      open,
      closed: expandable && !open,
      badges: node.badges.map(b => BADGE_TEXT[b]).filter((b): b is TreeBadgeView => b !== undefined),
      count: countOf(node, workshopPoints),
      countPeople: node.kind === 'D',
      selected: node.id === options.selectedId,
      moreCount: 0,
      moreGroups: false
    });
    if (!open) {
      return;
    }
    const mustShow = (k: TreeNodeModel): boolean => onPath.has(k.id) || (matches !== null && matches.has(k.id));
    const visible = options.showAll[node.id] === true || matches !== null || kids.length <= CHILD_LIMIT
      ? kids
      : kids.filter((k, i) => i < CHILD_LIMIT || mustShow(k));
    for (const kid of visible) {
      visit(kid, depth + 1);
    }
    const hidden = kids.filter(k => !visible.includes(k));
    if (hidden.length > 0) {
      rows.push({
        kind: 'more', id: node.id, node: null, depth: depth + 1, pad: `${8 + (depth + 1) * 15}px`, icon: 'group', color: '#6b7280', open: false, closed: false,
        badges: [], count: null, countPeople: false, selected: false, moreCount: hidden.length, moreGroups: hidden.every(h => h.kind === 'G')
      });
    }
  };

  for (const rootId of index.roots) {
    const root = index.byId.get(rootId);
    if (root && isLive(root)) {
      visit(root, 0);
    }
  }
  return rows;
}

function countOf(node: TreeNodeModel, workshopPoints: number): number | null {
  if (node.kind === 'G') {
    return node.storesBelow > 0 ? node.storesBelow : null;
  }
  if (node.kind === 'D') {
    return node.peopleHere > 0 ? node.peopleHere : null;
  }
  return workshopPoints > 0 ? workshopPoints : null;
}

/** The nodes to open so the selected one is seen: all its ancestors and the node itself. */
export function expandPathOf(index: TreeIndex, id: number | null, current: Readonly<Record<number, boolean>>): Record<number, boolean> {
  const next: Record<number, boolean> = { ...current };
  if (id !== null) {
    for (const parent of ancestorsOf(index, id)) {
      next[parent.id] = true;
    }
    next[id] = true;
  }
  return next;
}

export function titleOfNode(index: TreeIndex, id: number | null, lang: string): string {
  const node = id === null ? undefined : index.byId.get(id);
  return node ? titleIn(node.title, lang) : '';
}

export function refOf(node: TreeNodeModel): NodeRef {
  return { id: node.id, title: node.title, kind: node.kind };
}

export function titleOfRef(title: Title, lang: string): string {
  return titleIn(title, lang);
}

/** The closed nodes that are neither groups nor stores — the departments: the summary counts all kinds in `nodes` and the groups and the stores apart, so that the breakdown adds up. */
export function closedDepartments(closed: TreeSummary['closed']): number {
  return Math.max(0, closed.nodes - closed.groups - closed.stores);
}
